import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const RoadmapPhases = () => {
  const [progressWidth, setProgressWidth] = useState('0%');
  const [activePhase, setActivePhase] = useState('phase2');
  const animatedMetrics = useRef(new Set());

  // Animate progress bar on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgressWidth('45%');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Animate phase cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'scale(1)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.phase-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'scale(0.95)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  // Animate metrics on scroll
  useEffect(() => {
    const animateValue = (obj, start, end, duration, suffix = '') => {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        obj.innerHTML = value + suffix;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    };

    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !animatedMetrics.current.has(entry.target)) {
          animatedMetrics.current.add(entry.target);
          const value = entry.target.textContent;
          if (value.includes('%')) {
            const num = parseInt(value);
            animateValue(entry.target, 0, num, 1000, '%');
          }
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.metric-value').forEach(metric => {
      metricsObserver.observe(metric);
    });

    return () => metricsObserver.disconnect();
  }, []);

  // Smooth scroll to phase
  const scrollToPhase = (phaseId, event) => {
    event.preventDefault();
    const element = document.getElementById(phaseId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setActivePhase(phaseId);
    }
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
            overflow-x: hidden;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Timeline Container */
        .timeline-container {
            position: relative;
            padding: 3rem 0;
            margin: 3rem 0;
        }
        
        /* Timeline Line */
        .timeline-line {
            position: absolute;
            left: 50%;
            top: 0;
            bottom: 0;
            width: 3px;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
            transform: translateX(-50%);
            z-index: 1;
        }
        
        .timeline-line::before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            width: 20px;
            height: 20px;
            background: #667eea;
            border-radius: 50%;
            transform: translate(-50%, -10px);
            box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
        }
        
        .timeline-line::after {
            content: '🚀';
            position: absolute;
            bottom: -30px;
            left: 50%;
            font-size: 2rem;
            transform: translateX(-50%);
            animation: float 3s ease-in-out infinite;
        }
        
        @keyframes float {
            0%, 100% { transform: translateX(-50%) translateY(0); }
            50% { transform: translateX(-50%) translateY(-10px); }
        }
        
        /* Phase Cards */
        .phase-card {
            position: relative;
            width: 48%;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 2.5rem;
            margin-bottom: 4rem;
            z-index: 2;
            transition: all 0.3s;
        }
        
        .phase-card:nth-child(odd) {
            margin-left: auto;
            margin-right: 0;
        }
        
        .phase-card:nth-child(even) {
            margin-left: 0;
            margin-right: auto;
        }
        
        .phase-card:hover {
            transform: scale(1.02);
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Phase Status Badge */
        .phase-status {
            position: absolute;
            top: -15px;
            right: 20px;
            padding: 0.5rem 1.5rem;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: bold;
            text-transform: uppercase;
        }
        
        .status-completed {
            background: linear-gradient(135deg, #67e8a9 0%, #48bb78 100%);
            color: #0a0a0a;
        }
        
        .status-current {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .status-upcoming {
            background: rgba(255, 255, 255, 0.1);
            color: #888;
        }
        
        @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.5); }
            50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.8); }
        }
        
        /* Phase Header */
        .phase-header {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-bottom: 1.5rem;
        }
        
        .phase-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.8rem;
            flex-shrink: 0;
        }
        
        .phase-title {
            flex: 1;
        }
        
        .phase-title h2 {
            font-size: 1.8rem;
            margin-bottom: 0.5rem;
        }
        
        .phase-duration {
            color: #888;
            font-size: 1rem;
        }
        
        /* Features List */
        .features-list {
            list-style: none;
            margin: 1.5rem 0;
        }
        
        .features-list li {
            padding: 0.8rem 0;
            display: flex;
            align-items: start;
            gap: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .features-list li:last-child {
            border-bottom: none;
        }
        
        .feature-status {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 0.8rem;
        }
        
        .status-done {
            background: #67e8a9;
            color: #0a0a0a;
        }
        
        .status-progress {
            background: #667eea;
            color: white;
            animation: rotate 2s linear infinite;
        }
        
        .status-planned {
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.2);
        }
        
        @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        
        /* Metrics Grid */
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            margin-top: 1.5rem;
        }
        
        .metric-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1rem;
            text-align: center;
        }
        
        .metric-value {
            font-size: 1.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .metric-label {
            font-size: 0.8rem;
            color: #888;
            margin-top: 0.3rem;
        }
        
        /* Progress Overview */
        .progress-overview {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 20px;
            padding: 3rem;
            margin: 4rem 0;
            text-align: center;
        }
        
        .overall-progress {
            width: 100%;
            height: 40px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            overflow: hidden;
            margin: 2rem 0;
            position: relative;
        }
        
        .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            border-radius: 20px;
            position: relative;
            transition: width 1s ease-out;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding-right: 1rem;
        }
        
        .progress-percentage {
            color: white;
            font-weight: bold;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        /* Tech Stack Comparison */
        .comparison-section {
            margin: 4rem 0;
        }
        
        .comparison-table {
            width: 100%;
            border-collapse: collapse;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
        }
        
        .comparison-table th,
        .comparison-table td {
            padding: 1.2rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .comparison-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: bold;
        }
        
        .comparison-table tr:last-child td {
            border-bottom: none;
        }
        
        .comparison-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Benefits Cards */
        .benefits-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .benefit-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s;
        }
        
        .benefit-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.5);
        }
        
        .benefit-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
        }
        
        .benefit-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .benefit-description {
            color: #888;
            font-size: 0.9rem;
        }
        
        /* Interactive Elements */
        .phase-selector {
            display: flex;
            justify-content: center;
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .phase-button {
            padding: 1rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            color: white;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .phase-button:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: translateY(-2px);
        }
        
        .phase-button.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: transparent;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
            .timeline-line {
                left: 30px;
            }
            
            .phase-card {
                width: calc(100% - 60px);
                margin-left: 60px !important;
            }
            
            .phase-card:nth-child(even) {
                margin-left: 60px !important;
            }
        }
        
        @media (max-width: 768px) {
            .timeline-line {
                left: 20px;
            }
            
            .phase-card {
                width: calc(100% - 40px);
                margin-left: 40px !important;
            }
            
            .metrics-grid {
                grid-template-columns: 1fr;
            }
            
            .benefits-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Roadmap & Phases</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Feuille de Route</h1>
          <p className="page-subtitle">Evolution progressive de l'Agent RAG Hybride en 3 phases</p>
        </div>
        
        {/* Progress Overview */}
        <div className="progress-overview">
          <h2>Progression Globale du Projet</h2>
          <div className="overall-progress">
            <div className="progress-bar" style={{ width: progressWidth }}>
              <span className="progress-percentage">45%</span>
            </div>
          </div>
          <p>Phase 1 terminée ✅ | Phase 2 en cours 🚀 | Phase 3 planifiée 📅</p>
        </div>
        
        {/* Phase Selector */}
        <div className="phase-selector">
          <button 
            className={`phase-button ${activePhase === 'phase1' ? 'active' : ''}`}
            onClick={(e) => scrollToPhase('phase1', e)}
          >
            Phase 1 - MVP
          </button>
          <button 
            className={`phase-button ${activePhase === 'phase2' ? 'active' : ''}`}
            onClick={(e) => scrollToPhase('phase2', e)}
          >
            Phase 2 - Optimisation
          </button>
          <button 
            className={`phase-button ${activePhase === 'phase3' ? 'active' : ''}`}
            onClick={(e) => scrollToPhase('phase3', e)}
          >
            Phase 3 - Scale
          </button>
        </div>
        
        {/* Timeline */}
        <div className="timeline-container">
          <div className="timeline-line"></div>
          
          {/* Phase 1 */}
          <div className="phase-card" id="phase1">
            <div className="phase-status status-completed">Terminé ✅</div>
            <div className="phase-header">
              <div className="phase-icon">🎯</div>
              <div className="phase-title">
                <h2>Phase 1 - MVP</h2>
                <div className="phase-duration">2-4 semaines | Oct-Nov 2024</div>
              </div>
            </div>
            
            <p>Mise en place d'un système RAG simple mais fonctionnel pour valider l'approche et obtenir des retours rapides.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-done">✓</span>
                <span>RAG Simple avec LangChain + FAISS + GPT-4</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>Index vectoriel unifié pour tous les documents</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>Chunking standard (1000 tokens avec overlap)</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>API REST basique avec FastAPI</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>Support des profils utilisateurs (IT, BackOffice)</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">85%</div>
                <div className="metric-label">Précision</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 3s</div>
                <div className="metric-label">Latence</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">10k</div>
                <div className="metric-label">Documents</div>
              </div>
            </div>
          </div>
          
          {/* Phase 2 */}
          <div className="phase-card" id="phase2">
            <div className="phase-status status-current">En cours 🚀</div>
            <div className="phase-header">
              <div className="phase-icon">⚡</div>
              <div className="phase-title">
                <h2>Phase 2 - Optimisation</h2>
                <div className="phase-duration">1-2 mois | Déc 2024 - Jan 2025</div>
              </div>
            </div>
            
            <p>Amélioration des performances et de la précision avec recherche hybride et optimisations avancées.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-done">✓</span>
                <span>Recherche Hybride (Vector + BM25)</span>
              </li>
              <li>
                <span className="feature-status status-done">✓</span>
                <span>2 Index spécialisés (Technique + Compliance)</span>
              </li>
              <li>
                <span className="feature-status status-progress">⟳</span>
                <span>Cache Redis multi-niveaux</span>
              </li>
              <li>
                <span className="feature-status status-progress">⟳</span>
                <span>Re-ranking avec Cross-Encoder</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Monitoring et analytics de base</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Feedback loop pour amélioration continue</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">92%</div>
                <div className="metric-label">Précision visée</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 2s</div>
                <div className="metric-label">Latence cible</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">100k</div>
                <div className="metric-label">Documents</div>
              </div>
            </div>
          </div>
          
          {/* Phase 3 */}
          <div className="phase-card" id="phase3">
            <div className="phase-status status-upcoming">Planifié 📅</div>
            <div className="phase-header">
              <div className="phase-icon">🚀</div>
              <div className="phase-title">
                <h2>Phase 3 - Scale</h2>
                <div className="phase-duration">3-6 mois | Fév - Juillet 2025</div>
              </div>
            </div>
            
            <p>Passage à l'échelle avec architecture distribuée et fonctionnalités avancées pour un déploiement entreprise.</p>
            
            <ul className="features-list">
              <li>
                <span className="feature-status status-planned"></span>
                <span>Multi-agents spécialisés si nécessaire</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>GraphDB (Neo4j) pour relations complexes</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Fine-tuning d'un modèle spécifique banking</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Analytics avancées et ML pour prédiction</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Déploiement Kubernetes avec auto-scaling</span>
              </li>
              <li>
                <span className="feature-status status-planned"></span>
                <span>Support multi-région et disaster recovery</span>
              </li>
            </ul>
            
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">95%+</div>
                <div className="metric-label">Précision cible</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">&lt; 1.5s</div>
                <div className="metric-label">Latence P50</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">1M+</div>
                <div className="metric-label">Documents</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Benefits Section */}
        <h2 style={{ textAlign: 'center', margin: '4rem 0 2rem' }}>Bénéfices de l'Approche Progressive</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="benefit-icon">⚡</div>
            <div className="benefit-title">Time to Market Rapide</div>
            <div className="benefit-description">
              MVP fonctionnel en 2-4 semaines pour valider la valeur métier
            </div>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">📈</div>
            <div className="benefit-title">Amélioration Continue</div>
            <div className="benefit-description">
              Feedback utilisateur intégré à chaque phase pour optimiser
            </div>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">💰</div>
            <div className="benefit-title">ROI Progressif</div>
            <div className="benefit-description">
              Investissement mesuré avec validation à chaque étape
            </div>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">🛡️</div>
            <div className="benefit-title">Risque Maîtrisé</div>
            <div className="benefit-description">
              Évite l'over-engineering et s'adapte aux besoins réels
            </div>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">🎯</div>
            <div className="benefit-title">Focus Business</div>
            <div className="benefit-description">
              Chaque phase apporte de la valeur métier immédiate
            </div>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">🔄</div>
            <div className="benefit-title">Flexibilité</div>
            <div className="benefit-description">
              Possibilité d'ajuster la roadmap selon les retours
            </div>
          </div>
        </div>
        
        {/* Tech Stack Evolution */}
        <div className="comparison-section">
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Évolution de la Stack Technique</h2>
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Composant</th>
                <th>Phase 1 (MVP)</th>
                <th>Phase 2 (Optimisation)</th>
                <th>Phase 3 (Scale)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Orchestration</strong></td>
                <td>LangChain Basic</td>
                <td>LangChain + Custom Router</td>
                <td>Multi-Agent Framework</td>
              </tr>
              <tr>
                <td><strong>Vector DB</strong></td>
                <td>FAISS (local)</td>
                <td>FAISS + Qdrant</td>
                <td>Qdrant Cluster</td>
              </tr>
              <tr>
                <td><strong>Search</strong></td>
                <td>Vector Only</td>
                <td>Vector + BM25</td>
                <td>+ Graph + Metadata</td>
              </tr>
              <tr>
                <td><strong>Cache</strong></td>
                <td>In-memory</td>
                <td>Redis Single</td>
                <td>Redis Cluster</td>
              </tr>
              <tr>
                <td><strong>Model</strong></td>
                <td>GPT-4 API</td>
                <td>GPT-4 + Claude</td>
                <td>+ Fine-tuned Model</td>
              </tr>
              <tr>
                <td><strong>Monitoring</strong></td>
                <td>Basic Logs</td>
                <td>Prometheus + Grafana</td>
                <td>Full Observability Stack</td>
              </tr>
              <tr>
                <td><strong>Deployment</strong></td>
                <td>Single Server</td>
                <td>Docker Compose</td>
                <td>Kubernetes + GitOps</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Next Steps */}
        <div style={{ textAlign: 'center', margin: '4rem 0' }}>
          <h2 style={{ marginBottom: '2rem' }}>Prochaines Étapes</h2>
          <p style={{ color: '#888', marginBottom: '2rem' }}>
            Actuellement en Phase 2, nous optimisons les performances et la précision
          </p>
          <a href="technical-stack.html" className="phase-button active" style={{ display: 'inline-block' }}>
            Voir la Stack Technique Détaillée →
          </a>
        </div>
      </div>
    </>
  );
};

export default RoadmapPhases;